import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { _error, _log } from '@shared/aux_helper_environment';
import {
  InsightsSuggestionsService,
  InsightSuggestion,
  InsightSuggestionsResponse,
} from 'app/services/agents/insights-suggestions.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'insights-suggestions-container',
  template: `
    <div class="suggestions-container" *ngIf="shouldShow">
      <!-- Header -->
      <div class="suggestions-header">
        <div class="header-content">
          <mat-icon class="header-icon">lightbulb</mat-icon>
          <div class="header-text">
            <h3>{{ 'AI_CHAT.INSIGHTS.CONTAINER_TITLE' | pTranslate }}</h3>
            <p>{{ 'AI_CHAT.INSIGHTS.CONTAINER_DESC' | pTranslate }}</p>
          </div>
        </div>
        <button
          mat-icon-button
          (click)="toggleCollapsed()"
          [attr.aria-label]="isCollapsed ? ('AI_CHAT.INSIGHTS.EXPAND_ARIA' | pTranslate) : ('AI_CHAT.INSIGHTS.COLLAPSE_ARIA' | pTranslate)"
          class="toggle-btn"
        >
          <mat-icon>{{ isCollapsed ? 'expand_more' : 'expand_less' }}</mat-icon>
        </button>
      </div>

      <!-- Loading State -->
      <div *ngIf="isLoading" class="loading-state">
        <mat-spinner diameter="32"></mat-spinner>
        <span>{{ 'AI_CHAT.INSIGHTS.LOADING' | pTranslate }}</span>
      </div>

      <!-- Error State -->
      <div *ngIf="hasError && !isLoading" class="error-state">
        <mat-icon color="warn">error_outline</mat-icon>
        <span>{{ 'AI_CHAT.INSIGHTS.ERROR' | pTranslate }}</span>
        <button mat-button color="primary" (click)="loadSuggestions()">
          <mat-icon>refresh</mat-icon>
          {{ 'AI_CHAT.INSIGHTS.RETRY' | pTranslate }}
        </button>
      </div>

      <!-- Suggestions Grid -->
      <div *ngIf="!isLoading && !hasError && !isCollapsed" class="suggestions-grid">
        <div *ngIf="suggestions.length === 0" class="no-suggestions">
          <mat-icon>check_circle</mat-icon>
          <span>{{ 'AI_CHAT.INSIGHTS.NO_SUGGESTIONS' | pTranslate }}</span>
        </div>

        <insight-suggestion-card
          *ngFor="let suggestion of suggestions; trackBy: trackBySuggestion"
          [suggestion]="suggestion"
          [isProcessing]="getProcessingState(suggestion.id)"
          (approve)="onApproveSuggestion($event)"
          (reject)="onRejectSuggestion($event)"
        ></insight-suggestion-card>
      </div>

      <!-- Summary when collapsed -->
      <div *ngIf="isCollapsed && !isLoading && !hasError" class="collapsed-summary">
        <span *ngIf="suggestions.length > 0">
          {{ suggestions.length }}
          {{
            suggestions.length === 1
              ? ('AI_CHAT.INSIGHTS.SUGGESTIONS_AVAILABLE' | pTranslate)
              : ('AI_CHAT.INSIGHTS.SUGGESTIONS_AVAILABLE_PLURAL' | pTranslate)
          }}
          {{ suggestions.length === 1 ? ('AI_CHAT.INSIGHTS.AVAILABLE' | pTranslate) : ('AI_CHAT.INSIGHTS.AVAILABLE_PLURAL' | pTranslate) }}
        </span>
        <span *ngIf="suggestions.length === 0"> {{ 'AI_CHAT.INSIGHTS.NO_PENDING' | pTranslate }} </span>
      </div>
    </div>
  `,
  styleUrls: ['./insights-suggestions-container.component.scss'],
})
export class InsightsSuggestionsContainerComponent implements OnInit, OnDestroy {
  @Input() autoLoad = true;
  @Input() showOnlyPending = true;

  suggestions: InsightSuggestion[] = [];
  isLoading = false;
  hasError = false;
  isCollapsed = false;
  processingIds = new Set<number>();

  private _unsubscribeAll = new Subject<void>();

  constructor(private insightsSuggestionsService: InsightsSuggestionsService) {}

  ngOnInit(): void {
    _log('[InsightsSuggestionsContainer] Component initialized');

    if (this.autoLoad) {
      this.loadSuggestions();
    }
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next();
    this._unsubscribeAll.complete();
  }

  get shouldShow(): boolean {
    return this.isLoading || this.hasError || this.suggestions.length > 0;
  }

  loadSuggestions(): void {
    this.isLoading = true;
    this.hasError = false;

    _log('[InsightsSuggestionsContainer] Loading suggestions...');

    const request = this.showOnlyPending
      ? this.insightsSuggestionsService.getSuggestionsByStatus('pending_approval')
      : this.insightsSuggestionsService.getSuggestions();

    request.pipe(takeUntil(this._unsubscribeAll)).subscribe({
      next: (response: InsightSuggestionsResponse) => {
        this.suggestions = response.suggestions;
        this.isLoading = false;

        _log('[InsightsSuggestionsContainer] Loaded suggestions:', this.suggestions.length);
      },
      error: error => {
        _error('[InsightsSuggestionsContainer] Error loading suggestions:', error);
        this.hasError = true;
        this.isLoading = false;
      },
    });
  }

  onApproveSuggestion(suggestionId: number): void {
    _log('[InsightsSuggestionsContainer] Approving suggestion:', suggestionId);

    this.processingIds.add(suggestionId);

    this.insightsSuggestionsService
      .approveSuggestion(suggestionId)
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: response => {
          _log('[InsightsSuggestionsContainer] Suggestion approved:', response);
          this.processingIds.delete(suggestionId);

          // Actualizar el estado de la sugerencia
          const suggestion = this.suggestions.find(s => s.id === suggestionId);
          if (suggestion) {
            suggestion.suggestion_status = 'approved';
          }

          // Si solo mostramos pendientes, remover de la lista
          if (this.showOnlyPending) {
            this.suggestions = this.suggestions.filter(s => s.id !== suggestionId);
          }
        },
        error: error => {
          _error('[InsightsSuggestionsContainer] Error approving suggestion:', error);
          this.processingIds.delete(suggestionId);
        },
      });
  }

  onRejectSuggestion(suggestionId: number): void {
    _log('[InsightsSuggestionsContainer] Rejecting suggestion:', suggestionId);

    this.processingIds.add(suggestionId);

    this.insightsSuggestionsService
      .rejectSuggestion(suggestionId, 'Rechazada por el usuario')
      .pipe(takeUntil(this._unsubscribeAll))
      .subscribe({
        next: response => {
          _log('[InsightsSuggestionsContainer] Suggestion rejected:', response);
          this.processingIds.delete(suggestionId);

          // Actualizar el estado de la sugerencia
          const suggestion = this.suggestions.find(s => s.id === suggestionId);
          if (suggestion) {
            suggestion.suggestion_status = 'rejected';
          }

          // Si solo mostramos pendientes, remover de la lista
          if (this.showOnlyPending) {
            this.suggestions = this.suggestions.filter(s => s.id !== suggestionId);
          }
        },
        error: error => {
          _error('[InsightsSuggestionsContainer] Error rejecting suggestion:', error);
          this.processingIds.delete(suggestionId);
        },
      });
  }

  getProcessingState(suggestionId: number): boolean {
    return this.processingIds.has(suggestionId);
  }

  toggleCollapsed(): void {
    this.isCollapsed = !this.isCollapsed;
  }

  trackBySuggestion(index: number, suggestion: InsightSuggestion): number {
    return suggestion.id;
  }

  /**
   * Método público para recargar sugerencias desde el componente padre
   */
  refresh(): void {
    this.loadSuggestions();
  }
}
